import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUI } from '@/contexts/UIContext';
import { useProfile } from '@/hooks/useProfile';
import { useTheme } from '@/contexts/ThemeContext';
import girlWithLPlates from '@/assets/girl-with-l-plates.jpg';

export const Header: React.FC = () => {
  const { menuOpen, toggleMenu } = useUI();
  const { profile } = useProfile();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const isPublicPage = location.pathname === '/' || location.pathname === '/progress-tracker';

  const role = profile?.role?.toLowerCase();
  const logoTarget = role === 'student'
    ? '/student-home'
    : role === 'instructor' || role === 'admin'
      ? '/instructor'
      : '/';

  return (
    <header className="sticky top-0 z-[1000] w-full max-w-[100vw] bg-gradient-to-r from-violet/5 via-rose/5 to-sky/5 border-b border-violet/20 backdrop-blur-sm" style={{paddingTop: 'max(8px, env(safe-area-inset-top, 0px))'}}>
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4" style={{paddingLeft: 'max(12px, env(safe-area-inset-left, 0px))', paddingRight: 'max(12px, env(safe-area-inset-right, 0px))'}}>
        <div className="flex justify-between items-center min-h-[46px] sm:min-h-[52px] py-1.5">
          {/* Logo & Nav */}
          <div className="flex items-center gap-3 sm:gap-4 md:gap-6 min-w-0 flex-shrink">
            <Link to={logoTarget} className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
            <div className="flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 flex-shrink-0">
              <img 
                src={girlWithLPlates} 
                alt="Best-Drive logo – learner jumping with L-plates" 
                className="max-w-full max-h-full w-auto h-auto object-contain object-center block rounded"
                loading="eager"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
              <span className="font-bold text-base sm:text-lg md:text-xl bg-gradient-to-r from-violet via-rose to-sky bg-clip-text text-transparent truncate">
                {profile?.driving_school_name || 'Best-Drive'}
              </span>
            </Link>
            
            {/* Public Navigation */}
            {isPublicPage && (
              <nav className="hidden md:flex items-center gap-1">
                <Link 
                  to="/progress-tracker" 
                  className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    location.pathname === '/progress-tracker' 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  Progress Tracker
                </Link>
              </nav>
            )}
          </div>
          
          {/* Theme Toggle & Menu */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full flex-shrink-0"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMenu}
              className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0"
              aria-label="Toggle navigation menu"
            >
              <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
            </Button>
          </div>
          
        </div>
      </div>
    </header>
  );
};